<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import qrcode from 'qrcode-generator';

/**
 * Exibição do segredo entregue UMA vez.
 *
 * Ele fica só nesta variável de componente, enquanto a tela estiver aberta.
 * Nada aqui grava em localStorage, sessionStorage ou cookie: perder a tela é
 * perder o segredo, e a recuperação é uma nova rotação — que é exatamente o
 * comportamento do servidor (DB12).
 *
 * Com `packageJson` (aparelho real), mostra o pacote de provisionamento que o
 * app Android aceita, com cópia e QR gerado aqui mesmo: o pacote nunca sai do
 * navegador para gerar imagem.
 */
const props = defineProps<{ secret: string | null; packageJson?: string | null }>();
const emit = defineEmits<{ dismiss: [] }>();

const copyMessage = ref<string | null>(null);
watch(() => props.packageJson, () => { copyMessage.value = null; });

const qrDataUrl = computed(() => {
  if (!props.packageJson) return null;
  // Tipo automático, correção M; margem de 4 módulos é a zona quieta da norma.
  const code = qrcode(0, 'M');
  code.addData(props.packageJson, 'Byte');
  code.make();
  return code.createDataURL(6, 4);
});

async function copyPackage(): Promise<void> {
  try {
    if (!props.packageJson || navigator.clipboard === undefined) throw new Error('clipboard');
    await navigator.clipboard.writeText(props.packageJson);
    copyMessage.value = 'Pacote copiado.';
  } catch {
    copyMessage.value = 'Não foi possível copiar automaticamente. Selecione o texto acima e copie manualmente.';
  }
}
</script>

<template>
  <section
    v-if="secret && packageJson"
    class="secret"
    data-testid="secret-once"
  >
    <h3>Pacote de acesso do aparelho</h3>
    <p>
      Ele aparece <strong>uma única vez</strong> e não fica armazenado em lugar
      nenhum. Leia o QR no aparelho ou cole o pacote em <em>Configurar aparelho</em>.
      Não fotografe, não compartilhe e não salve em arquivos comuns. Se perder,
      rotacione a chave.
    </p>
    <code data-testid="provisioning-package">{{ packageJson }}</code>
    <div class="actions">
      <button
        type="button"
        @click="copyPackage"
      >
        Copiar pacote
      </button>
      <button
        type="button"
        @click="emit('dismiss')"
      >
        Já provisionei / Fechar
      </button>
    </div>
    <p
      v-if="copyMessage !== null"
      role="status"
    >
      {{ copyMessage }}
    </p>
    <img
      v-if="qrDataUrl !== null"
      class="qr"
      :src="qrDataUrl"
      alt="QR do pacote de acesso do aparelho"
      data-testid="provisioning-qr"
    >
  </section>
  <section
    v-else-if="secret"
    class="secret"
    data-testid="secret-once"
  >
    <h3>Chave do dispositivo</h3>
    <p>
      Copie agora. Ela aparece <strong>uma única vez</strong> e não fica armazenada
      em lugar nenhum — nem aqui, nem no servidor. Se perder, emita uma rotação nova.
    </p>
    <code>{{ secret }}</code>
    <button
      type="button"
      @click="emit('dismiss')"
    >
      Já copiei
    </button>
  </section>
</template>

<style scoped>
.secret { border: 2px solid #f9ab00; padding: 1rem; border-radius: 4px; background: #fffbe6; }
code { display: block; word-break: break-all; margin: .5rem 0; user-select: all; }
.actions { display: flex; gap: .5rem; }
.qr { display: block; max-width: 100%; height: auto; margin-top: .75rem; background: #fff; image-rendering: pixelated; }
</style>
